/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import { auditLogService } from '../services/api';
import { AuditLog } from '../types';

interface UseAuditLogsOptions {
  /** Projects to read. The audit API is per-project, so "all projects" fans out one request per project. */
  projectIds: string[];
  action?: string;
  resourceType?: string;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Audit logs for one or more projects, merged and sorted newest-first.
 */
export const useAuditLogs = ({
  projectIds,
  action,
  resourceType,
  pageSize = 100,
  enabled = true,
}: UseAuditLogsOptions) => {
  const result = useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ['audit-logs', projectId, action || 'all', resourceType || 'all', pageSize],
      queryFn: () =>
        auditLogService
          .list(projectId, {
            page: 1,
            page_size: pageSize,
            action: action && action !== 'all' ? action : undefined,
            resource_type: resourceType && resourceType !== 'all' ? resourceType : undefined,
          })
          .then((res) => res.data),
      enabled: enabled && !!projectId,
    })),
    combine: (results) => {
      const logs: AuditLog[] = results
        .flatMap((r) => (r.data?.logs as AuditLog[] | undefined) || [])
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      return {
        logs,
        isLoading: results.some((r) => r.isLoading),
        isFetching: results.some((r) => r.isFetching),
        isError: results.length > 0 && results.every((r) => r.isError),
        refetchers: results.map((r) => r.refetch),
      };
    },
  });

  const { refetchers } = result;
  const refetch = useCallback(() => {
    refetchers.forEach((fn) => fn());
  }, [refetchers]);

  return { ...result, refetch };
};

export default useAuditLogs;
